import { createListenerMiddleware, createSlice, current, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { dataWranglerApiSlice } from "../../api/dataWranglerApiSlice";
import MapWizardToQuery from "../../mappers/WizardToQuery";
import { getBidsDataTypes } from "./UtilityFunctions";
import { setNewNotification } from "../common/notificationSlice";
import { QueryBuilderInstrumentsTypes } from "../../features/data-wrangler/types/QueryBuilderInstrumentTypes";
import { setSelectedDatabase, selectSelectedStudy, selectSelectedDatabase } from '../../features/common/databaseSlice';
import { sendNativePost } from "../../utils";

export type QueryTools = "queryWizard" | "dictionary" | "allStudyData";

export const defaultOperators = {
    visit: 'OR',
    instrument: 'AND'
}

const initialAdvanced = {
    selectedFields: {},
    selectedFiles: {},
    selectedFileGroups: [],
    participantLimit: 0,
    summary: {
        raw: {
            fileCount: 0,
            fileSize: '0 kB'
        },
        derivative: {
            fileCount: 0,
            fileSize: '0 kB'
        },
        concat: {
            fileCount: 0,
            fileSize: '0 kB'
        },
        sourcedata: {
            fileCount: 0,
            fileSize: '0 kB'
        }
    }
}

const initialState = {
    visits: [],
    summary: {},
    wizard: {
        ageRange: [],
        constructs: [],
        instruments: [],
        fieldType: '',
        operators: defaultOperators
    },
    advanced: initialAdvanced,
    loading: false,
    selectedQuery: -1,
    dashboardData: {},
    summaryRunning: {
        active: false,
        rerun: false
    },
    fileSummary: {
        loading: false,
        fileCount: 0,
        fileSize: '0 kB',
        fileCountDetails: {},
        globusCount: 0
    },
    selectedQueryTool: 'allStudyData',
    isRedirectedFromDD: false,
    dataDictionaryModel: {},
    participantFilter: [],
}

const dataWranglerSlice = createSlice({
    name: 'dataWrangler',
    initialState: initialState as any,
    reducers: {
        // We do this so it does not mess up with shadow matrix checkbox
        reset: (state, action) => {
            const exempted = {}
            const exemptedKeys = action.payload.exemptKeys || [];
            const currentState = current(state);
            for(const i of Object.keys(exemptedKeys)) {
                // @ts-ignore
                exempted[i] = currentState[i];
            }
            return {
                ...initialState,
                ...exempted,
                isRedirectedFromDD: state.isRedirectedFromDD,
            }
        },
        resetDatabase: (state) => {
            state.wizard = initialState.wizard;
            state.advanced = initialState.advanced;
        },
        setSummary: (state, action) => {
            state.summary = action.payload?.data;
        },
        addAgeRange: (state, action: PayloadAction<string>) => {
            state.wizard.ageRange = [
                ...state.wizard.ageRange,
                action.payload
            ];
        },
        removeAgeRange: (state, action: PayloadAction<string>) => {
            state.wizard.ageRange = state.wizard.ageRange.filter((range: any) => range !== action.payload);
        },
        setAgeRange: (state, action: PayloadAction<string[]>) => {
            state.wizard.ageRange = action.payload;
        },
        addConstruct: (state, action: PayloadAction<string>) => {
            state.wizard.constructs = [
                ...state.wizard.constructs,
                action.payload
            ];
        },
        removeConstruct: (state, action: PayloadAction<string>) => {
            state.wizard.constructs = state.wizard.constructs.filter((construct: any) => construct !== action.payload);
        },
        setConstructs: (state, action: PayloadAction<string[]>) => {
            state.wizard.constructs = action.payload;
        },
        addInstrument: (state, action: PayloadAction<string>) => {
            state.wizard.instruments = [
                ...state.wizard.instruments,
                action.payload
            ];
        },
        removeInstrument: (state, action: PayloadAction<string>) => {
            state.wizard.instruments = state.wizard.instruments.filter((construct: any) => construct !== action.payload);
        },
        setInstruments: (state, action: PayloadAction<string[]>) => {
            state.wizard.instruments = action.payload;
        },
        setFieldType: (state, action: PayloadAction<string>) => {
            state.wizard.fieldType = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setRunning: (state, action: PayloadAction<boolean>) => {
            state.summaryRunning.active = action.payload;
        },
        setRerun: (state, action: PayloadAction<boolean>) => {
            state.summaryRunning.rerun = action.payload;
        },
        selectQuery: (state, action: PayloadAction<number>) => {
            state.selectedQuery = action.payload;
        },
        setDashboardData: (state, action: PayloadAction<any>) => {
            state.dashboardData = action.payload;
        },
        loadQuery: (state, action: PayloadAction<any>) => {
            state.wizard = {
                ageRange: action.payload.ageRange,
                constructs: action.payload.constructs,
                instruments: action.payload.instruments,
                fieldType: ''
            }
        },
        setAdvancedFields: (state, action: PayloadAction<any>) => {
            state.advanced.selectedFields = action.payload;
        },
        adwAddFields: (state, action: PayloadAction<any[]>) => {
            const fields = state.advanced.selectedFields;
            action.payload.forEach((field) => {
                if (!fields[field.table_name]) {
                    fields[field.table_name] = {
                        id: field.id,
                        name: field.table_name,
                        fields: {},
                        ageGroups: []
                    }
                }

                fields[field.table_name]['fields'][field.name] = field;
            });

            state.advanced.selectedFields = fields;
        },
        adwRemoveField: (state, action: PayloadAction<any>) => {
            const fields = state.advanced.selectedFields;
            delete fields[action.payload.instrument]['fields'][action.payload.field];
            if (Object.keys(fields[action.payload.instrument]['fields'] || {}).length === 0) {
                delete fields[action.payload.instrument]
            }
            state.advanced.selectedFields = fields;
        },
        adwRemoveInstrument: (state, action: PayloadAction<string>) => {
            const fields = state.advanced.selectedFields;
            delete fields[action.payload];
            state.advanced.selectedFields = fields;
        },
        adwUpdateAgeGroups: (state, action: PayloadAction<any>) => {
            const fields = state.advanced.selectedFields;
            fields[action.payload.instrument]['ageGroups'] = action.payload.ageGroups;
            state.advanced.selectedFields = fields;
        },
        adwResetFields: (state) => {
            state.advanced.selectedFields = {};
        },
        // eslint-disable-next-line
        adwRunQuery: (state, action: PayloadAction<any>) => {
            state.loading = true;
        },
        adwDownloadQueryDictionary: (state) => {
            state.loading = true;
        },
        adwSaveQuery: (state) => {
            state.loading = true;
        },
        // eslint-disable-next-line
        transferFiles: (state, payload) => {
            state.loading = true;
        },
        adwTransferFiles: (state) => {
            state.loading = true;
        },
        // eslint-disable-next-line
        queueInitiated: (state, payload) => {
            state.loading = true;
        },
        transferFilesSummary: (state) => {
            state.fileSummary = {
                loading: true,
                raw: {
                    fileCount: 0,
                    fileSize: '0 kB'
                },
                derivative: {
                    fileCount: 0,
                    fileSize: '0 kB'
                },
                sourcedata: {
                    fileCount: 0,
                    fileSize: '0 kB'
                }
            }
        },
        setTransferFilesSummary: (state, action: PayloadAction<any>) => {
            state.fileSummary = {
                loading: false,
                fileCount: action.payload.fileCount,
                fileSize: action.payload.fileSize,
                participantCount: action.payload.participantCount,
                fileCountDetails: action.payload.fileCountDetails,
                derivativeCountDetails: action.payload.derivativeCountDetails,
                globusCount: action.payload.globusCount,
            }
        },
        setTransferFilesSummaryLoading: (state, action: PayloadAction<any>) => {
            state.fileSummary.loading = action.payload;
        },
        adwUpdateSelectedFile: (state, action: PayloadAction<any>) => {
            state.advanced.selectedFiles = action.payload;
        },
        setSelectedFileGroups: (state, action: PayloadAction<any>) => {
            state.advanced.selectedFileGroups = action.payload;
        },
        adwResetSelectedFile: (state) => {
            state.advanced.selectedFiles = initialState.advanced.selectedFiles;
            state.fileSummary = initialState.fileSummary;
        },
        adwUpdateFileSummary: (state, action: PayloadAction<any>) => {
            state.advanced.summary[action.payload.type] = {
                fileCount: action.payload.data.count,
                fileSize: action.payload.data.size,
            }
        },
        setSelectedQueryTool: (state, action: PayloadAction<QueryTools>) => {
            state.selectedQueryTool = action.payload;
        },
        setQueryWizard: (state, action: PayloadAction<any>) => {
            state.wizard = action.payload;
        },
        setWizardOperators: (state, action: PayloadAction<any>) => {
            state.wizard.operators[action.payload.field] = action.payload.value;
        },
        setIsRedirected: (state, action: PayloadAction<any>) => {
            state.isRedirectedFromDD = action.payload;
        },
        removeItemsFromSelection: (state, action: PayloadAction<{ visits: string[], instruments: string[], constructs: string[] }>) => {
            const { visits, instruments, constructs } = action.payload;
            if(visits.length > 0) {
                state.wizard.ageRange = deleteFromArray(state.wizard.ageRange, visits)
            }
            if(instruments.length > 0) {
                state.wizard.instruments = deleteFromArray(state.wizard.instruments, instruments)
            }
            if(constructs.length > 0) {
                state.wizard.constructs = deleteFromArray(state.wizard.constructs, constructs)
            }
        },
        setDataDictionaryModel: (state, action: PayloadAction<any>) => {
            state.dataDictionaryModel = action.payload;
        },
        setParticipantLimit: (state, action: PayloadAction<any>) => {
            state.participantLimit = action.payload;
        },
        setParticipantFilter: (state, action: PayloadAction<string[]>) => {
            state.participantFilter = action.payload
        }
    }
});

function deleteFromArray(original: string[], itemsToDelete: string[] ): string[] {
    const originalSet = new Set(original);
    for (const itemToDelete of itemsToDelete) {
        originalSet.delete(itemToDelete);
    }
    return Array.from(originalSet);
}

export const {
    reset,
    resetDatabase,
    setSummary,
    addAgeRange,
    removeAgeRange,
    setAgeRange,
    addConstruct,
    removeConstruct,
    setConstructs,
    addInstrument,
    removeInstrument,
    setInstruments,
    setFieldType,
    setLoading,
    setRunning,
    setRerun,
    selectQuery,
    setDashboardData,
    loadQuery,
    adwAddFields,
    adwRemoveField,
    adwRemoveInstrument,
    adwUpdateAgeGroups,
    adwResetFields,
    adwRunQuery,
    adwDownloadQueryDictionary,
    transferFiles,
    transferFilesSummary,
    setTransferFilesSummary,
    setTransferFilesSummaryLoading,
    adwUpdateSelectedFile,
    setSelectedFileGroups,
    adwResetSelectedFile,
    adwUpdateFileSummary,
    adwTransferFiles,
    queueInitiated,
    setSelectedQueryTool,
    setQueryWizard,
    setAdvancedFields,
    setWizardOperators,
    setIsRedirected,
    removeItemsFromSelection,
    setDataDictionaryModel,
    setParticipantLimit,
    setParticipantFilter
} = dataWranglerSlice.actions;

export default dataWranglerSlice.reducer;

export const selectSummary = (state: any) => state.dataWrangler.summary;
export const selectWizardState = (state: any) => state.dataWrangler.wizard;
export const selectAgeRange = (state: any) => state.dataWrangler.wizard.ageRange;
export const selectConstructs = (state: any) => state.dataWrangler.wizard.constructs;
export const selectInstruments = (state: any) => state.dataWrangler.wizard.instruments;
export const selectFieldType = (state: any) => state.dataWrangler.wizard.fieldType;
export const selectLoading = (state: any) => state.dataWrangler.loading;
export const selectRunning = (state: any) => state.dataWrangler.summaryRunning;
export const selectSelectedQuery = (state: any) => state.dataWrangler.selectedQuery;
export const selectDashboardData = (state: any) => state.dataWrangler.dashboardData;
export const selectAdvancedFields = (state: any) => state.dataWrangler.advanced.selectedFields;
export const selectAdvancedFiles = (state: any) => state.dataWrangler.advanced.selectedFiles;
export const selectSelectedFileGroups = (state: any) => state.dataWrangler.advanced.selectedFileGroups;
export const selectFileSummary = (state: any) => state.dataWrangler.fileSummary;
export const selectSelectedQueryTool = (state: any) => state.dataWrangler.selectedQueryTool
export const selectIsRedirected = (state: any): string => state.dataWrangler.isRedirectedFromDD;
export const selectWizardOperators = (state: any) => state.dataWrangler.wizard.operators;
export const selectDataDictionaryModel = (state: any) => state.dataWrangler.dataDictionaryModel;
export const selectParticipantLimit = (state: any) => state.dataWrangler.participantLimit;
export const selectParticipantFilter = (state: any) => state.dataWrangler.participantFilter;

const dwListenerMiddleware = createListenerMiddleware();

dwListenerMiddleware.startListening({
    matcher: isAnyOf(addAgeRange, removeAgeRange, setAgeRange, addConstruct, removeConstruct, setConstructs, addInstrument, removeInstrument, setInstruments, loadQuery, setRerun, setQueryWizard, setWizardOperators, setParticipantFilter),
    effect: async (action: any, listenerApi: any) => {
        const tool = selectSelectedQueryTool(listenerApi.getState());
        if (tool !== 'queryWizard') return;
        let running = selectRunning(listenerApi.getState());
        if (running.active) {
            if (!running.rerun) {
                listenerApi.dispatch(setRerun(true));
            }
            return;
        }
        listenerApi.dispatch(setRunning(true));
        const selectedDatabase = selectSelectedDatabase(listenerApi.getState());
        const wizardConfig = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.wizard.initiate({ database: selectedDatabase }));

        const selectAges = selectAgeRange(listenerApi.getState())
        const selectedConstructs = selectConstructs(listenerApi.getState());
        const selectedInstruments = selectInstruments(listenerApi.getState());
        const selectedOperators = selectWizardOperators(listenerApi.getState());

        const query = MapWizardToQuery(wizardConfig.data, selectAges, selectedConstructs, selectedInstruments, selectedOperators);
        query['ParticipantFilter'] = selectParticipantFilter(listenerApi.getState());

        const summary = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.summary.initiate(query));
        listenerApi.dispatch(setSummary(summary));
        listenerApi.dispatch(setRunning(false));
        running = selectRunning(listenerApi.getState());
        if (running.rerun) {
            listenerApi.dispatch(setRerun(false));
        }
    }
});

dwListenerMiddleware.startListening({
    matcher: isAnyOf(transferFiles, transferFilesSummary),
    effect: async (action, listenerApi) => {
        const selectedDatabase = selectSelectedDatabase(listenerApi.getState());
        const wizardConfig = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.wizard.initiate({ database: selectedDatabase }));

        const selectAges = selectAgeRange(listenerApi.getState())
        const selectedConstructs = selectConstructs(listenerApi.getState());
        const selectedInstruments = selectInstruments(listenerApi.getState());
        const selectedOperators = selectWizardOperators(listenerApi.getState());

        const bidsDataTypes = getBidsDataTypes(wizardConfig.data.bids_files?.raw);
        const fileTypes: any[] = [];
        const derivatives: any[] =
            selectedInstruments.filter((inst: string) => inst.includes('bderivative_'))
                .map((inst: string) => inst.replace('bderivative_', ''));
        const sourcedata: any[] =
            selectedInstruments.filter((inst: string) => inst.includes('bsourcedata_'))
                .map((inst: string) => inst.replace('bsourcedata_', ''));

        selectedInstruments.filter((inst: string) => inst.includes('bfile_')).forEach((inst: string) => {
            bidsDataTypes[inst.replace('bfile_', '')].forEach((fileType: any) => {
                fileTypes.push(fileType.suffix);
            });
        })

        const query: any = {
            'query': MapWizardToQuery(wizardConfig.data, selectAges, selectedConstructs, selectedInstruments, selectedOperators),
            'file_types': fileTypes,
            'derivatives': derivatives,
            'sourcedata': sourcedata,
        };

        if (transferFiles.match(action)) {
            query['data_provider'] = action.payload.data_provider;
            query['bids_type'] = action.payload.bids_type;

            const filelist = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.transferFiles.initiate(query));
            localStorage.setItem("globus-file-list", JSON.stringify(filelist));
            listenerApi.dispatch(setLoading(false));
        } else {
            const response: {
                data: any
            } | any = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.transferFilesSummary.initiate(query));
            if (response.data) {
                listenerApi.dispatch(setTransferFilesSummary(response.data));
            }
        }
    }
});

dwListenerMiddleware.startListening({
    matcher: isAnyOf(adwTransferFiles),
    effect: async (action: any, listenerApi) => {
        const body: any = {
            query: selectAdvancedFiles(listenerApi.getState()),
            summary: selectFileSummary(listenerApi.getState()),
        };
        localStorage.setItem("globus-file-list", JSON.stringify(body));
        localStorage.setItem("participant-filter", JSON.stringify(selectParticipantFilter(listenerApi.getState())));
        listenerApi.dispatch(setLoading(false));
    }
});

dwListenerMiddleware.startListening({
    matcher: isAnyOf(adwUpdateSelectedFile, setParticipantFilter),
    effect: async (action: any, listenerApi) => {

        const tool = selectSelectedQueryTool(listenerApi.getState());
        if (tool !== 'allStudyData') return;

        listenerApi.cancelActiveListeners()

        await listenerApi.delay(100)

        // set the entity filters for EEG, MOTION
        const payload = JSON.parse(JSON.stringify({
            query: selectAdvancedFiles(listenerApi.getState()),
            participants: selectParticipantFilter(listenerApi.getState())
        }));
        listenerApi.dispatch(setTransferFilesSummaryLoading(true));
        const response: {
            data: any
        } | any = await listenerApi.pause(listenerApi.dispatch(dataWranglerApiSlice.endpoints.adwTransferFilesSummary.initiate(payload)));
        localStorage.setItem("globus-file-summary", JSON.stringify(response?.data ?? ""));
        listenerApi.dispatch(setTransferFilesSummaryLoading(false));
        if (response.data) {
            listenerApi.dispatch(setTransferFilesSummary(response.data));
        }
    }
});

dwListenerMiddleware.startListening({
    matcher: isAnyOf(queueInitiated),
    effect: async (action: any, listenerApi) => {
        if (queueInitiated.match(action)) {
            const { type, client } = action.payload;
            const participantLimit = selectParticipantLimit(listenerApi.getState());

            const response1: any = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.adwTransferFiles.initiate({
                type: type,
                selected: selectAdvancedFiles(listenerApi.getState()),
                participantLimit: Number.isInteger(participantLimit) ? participantLimit : null,
            }));

            const fileList = response1 && response1.data && response1.data.length > 0 ? response1.data.map((file: any) => file.filename) : []
            const response2: any = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.downloadQueueQuery.initiate({
                "client": client,
                "sender": "TBD",
                files: fileList,
                study: selectSelectedStudy(listenerApi.getState())?.toLowerCase(),
            }));

            if (response2?.data && response2?.data === 200) {
                listenerApi.dispatch(setNewNotification({
                    message: "Your request has been queued. Your file transfer will start as soon as possible",
                    type: "success"
                }))
            } else {
                listenerApi.dispatch(setNewNotification({
                    message: "It was not possible to send your request.",
                    type: "error"
                }))
            }

            listenerApi.dispatch(setLoading(false));
        }
    }
})

dwListenerMiddleware.startListening({
    actionCreator: setFieldType,
    effect: async (action, listenerApi) => {
        listenerApi.dispatch(setLoading(true));
        const selectedDatabase = selectSelectedDatabase(listenerApi.getState());
        const wizardConfig = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.wizard.initiate({ database: selectedDatabase }));

        const visits: string[] = [];
        const selectAges = selectAgeRange(listenerApi.getState());
        const selectedInstruments = selectInstruments(listenerApi.getState());
        const selectedFieldType = selectFieldType(listenerApi.getState());
        const selectedOperators = selectWizardOperators(listenerApi.getState());
        const selectedConstructs = selectConstructs(listenerApi.getState());

        wizardConfig.data.ageGroups.forEach((group: any) => {
            if (selectAges.includes(group.label)) {
                visits.push(group.label);
            }
        });

        const summaryQuery = MapWizardToQuery(wizardConfig.data, selectAges, selectedConstructs, selectedInstruments, selectedOperators);

        const query: any = {
            Instruments: [],
            StaticInstruments: [],
            Visits: visits,
            FieldType: selectedFieldType,
            Operators: summaryQuery.Operators,
            Conditions: summaryQuery.Conditions,
            ParticipantFilter: selectParticipantFilter(listenerApi.getState())
        };

        summaryQuery.Conditions.children.forEach((condition: any) => {
            if (condition.field === QueryBuilderInstrumentsTypes.Instrument) {
                query.Instruments.push(condition.operator[0]);
            } else if (condition.field === QueryBuilderInstrumentsTypes.StaticInstrument) {
                query.StaticInstruments.push(condition.operator[0])
            }
        })

        const fieldType = selectFieldType(listenerApi.getState());

        if (fieldType === 'shadow') {
            sendNativePost('data_wrangler/wizard/shadow', query);
        } else {
            sendNativePost('data_wrangler/wizard', query);
        }
    }
});

dwListenerMiddleware.startListening({
    actionCreator: selectQuery,
    effect: async (action, listenerApi) => {
        const savedQueries = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.getSavedQueries.initiate({}));
        const sQuery = savedQueries.data?.find((query: any) => query.id === action.payload);
        if (sQuery.query.ageRange.length === 0) {
            const summary = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.dashboard.initiate({}));
            listenerApi.dispatch(setDashboardData(summary));
        } else {
            const selectedDatabase = selectSelectedDatabase(listenerApi.getState());
            const wizardConfig = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.wizard.initiate({ database: selectedDatabase }));
            const selectedOperators = sQuery.query.operators ?? defaultOperators
            const query = MapWizardToQuery(wizardConfig.data, sQuery.query.ageRange, sQuery.query.constructs, sQuery.query.instruments, selectedOperators);
            const summary = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.summary.initiate(query));
            listenerApi.dispatch(setDashboardData(summary));
        }
    }
});

dwListenerMiddleware.startListening({
    actionCreator: adwRunQuery,
    effect: async (action, listenerApi) => {
        const isShadow = action.payload
        listenerApi.dispatch(setLoading(true));
        const selectedDatabase = selectSelectedDatabase(listenerApi.getState());
        const wizardConfig = await listenerApi.dispatch(dataWranglerApiSlice.endpoints.wizard.initiate({ database: selectedDatabase }));
        const fields = selectAdvancedFields(listenerApi.getState());

        const allVisits: string[]
            = wizardConfig.data.ageGroups.map((group: any) => group.label);

        const requestFields: any = {};
        Object.keys(fields || {}).forEach((inst: string) => {
            Object.keys(fields[inst].fields || {}).forEach((fieldKey: string) => {
                // Initialize the object for this instrument if it doesn't already exist
                if (!requestFields[inst]) {
                    requestFields[inst] = {
                        instrument: inst,
                        fields: [],
                        visits: [],
                        staticInstrument: wizardConfig.data.allInstruments[inst].staticInstrument,
                    };
                }

                // Add the field key to the fields array
                requestFields[inst].fields.push(fieldKey);

                // Set the visits array based on the ageGroups
                if (fields[inst].ageGroups.length === 0) {
                    requestFields[inst].visits = allVisits;
                } else {
                    requestFields[inst].visits = fields[inst].ageGroups
                        .map((group: any) => group.value.split(','))
                        .flat();
                }
            });
        });

        const query: any = {
            fields: Object.values(requestFields),
            participants: selectParticipantFilter(listenerApi.getState())
        }
        if (isShadow) {
            sendNativePost('data_wrangler/advanced/shadow', query);
        } else {
            sendNativePost('data_wrangler/advanced', query);
        }
    }
})

dwListenerMiddleware.startListening({
    matcher: isAnyOf(setSelectedDatabase),
    effect: async (action, listenerApi) => {
        listenerApi.dispatch(resetDatabase());
    }
});

dwListenerMiddleware.startListening({
    matcher: isAnyOf(setDataDictionaryModel),
    effect: async (action, listenerApi) => {
        const dataDictionaryModel = selectDataDictionaryModel(listenerApi.getState());
        sendNativePost('datadict/download/zip', dataDictionaryModel);
    }
});

dwListenerMiddleware.startListening({
    matcher: isAnyOf(adwDownloadQueryDictionary),
    effect: async (action, listenerApi) => {
        const ids: string[] = [];
        const instruments = selectAdvancedFields(listenerApi.getState());

        Object.keys(instruments).forEach((inst: string) => {
            const fields = instruments[inst].fields;
            Object.keys(fields).forEach((field: string) => {
                ids.push(fields[field].id.toString());
            })
        });
        sendNativePost('datadict/query/download/zip', ids);
    }
});

export { dwListenerMiddleware };